const STORAGE_KEY_PREFIX = "savedPalette_section";

export const MAX_SAVED_PALETTES = 5;

export const SAVED_PALETTES_UPDATED_EVENT = "savedPalettesUpdated";

const normalizeScopeKey = (scopeKey) => {
  if (typeof scopeKey !== "string") return "global";

  const trimmedScopeKey = scopeKey.trim();
  return trimmedScopeKey || "global";
};

const isValidPalette = (palette) => (
  Array.isArray(palette) &&
  palette.length > 0 &&
  palette.every((color) => typeof color === "string" && color.trim())
);

export const buildSavedPalettesStorageKey = (scopeKey) => (
  `${STORAGE_KEY_PREFIX}:${normalizeScopeKey(scopeKey)}`
);

const dispatchSavedPalettesUpdate = (scopeKey, palettes) => {
  if (typeof window === "undefined") return;

  window.dispatchEvent(new CustomEvent(SAVED_PALETTES_UPDATED_EVENT, {
    detail: {
      scopeKey: normalizeScopeKey(scopeKey),
      palettes,
    },
  }));
};

/**
 * Read saved palettes from localStorage for a given scope.
 * Handles migration from old single-palette format (array of strings)
 * to new multi-palette format (array of arrays of strings).
 */
const readPalettesFromStorage = (scopeKey) => {
  const stored = localStorage.getItem(buildSavedPalettesStorageKey(scopeKey));
  if (!stored) return [];

  const parsed = JSON.parse(stored);
  if (!Array.isArray(parsed) || parsed.length === 0) return [];

  // Old single-palette format: ["#hex", "#hex", "#hex", "#hex"]
  if (typeof parsed[0] === "string") {
    return isValidPalette(parsed) ? [parsed] : [];
  }

  // New multi-palette format: [["#hex", ...], ["#hex", ...], ...]
  return parsed.filter(isValidPalette).slice(0, MAX_SAVED_PALETTES);
};

export const getSavedPalettes = (scopeKey) => {
  try {
    return readPalettesFromStorage(scopeKey);
  } catch (error) {
    console.warn("Failed to load saved palettes from localStorage:", error);
    return [];
  }
};

export const savePalette = (scopeKey, palette) => {
  if (!isValidPalette(palette)) return null;

  try {
    const normalizedPalette = palette.map((color) => color.trim());
    const existing = getSavedPalettes(scopeKey);

    // Prevent duplicates by comparing normalized color strings
    const signature = normalizedPalette.join("|");
    const isDuplicate = existing.some(
      (p) => p.map((c) => c.trim()).join("|") === signature,
    );
    if (isDuplicate) return existing;

    // Add new palette at front; oldest is dropped when limit is reached
    const updated = [normalizedPalette, ...existing].slice(0, MAX_SAVED_PALETTES);

    localStorage.setItem(
      buildSavedPalettesStorageKey(scopeKey),
      JSON.stringify(updated),
    );
    dispatchSavedPalettesUpdate(scopeKey, updated);
    return updated;
  } catch (error) {
    console.warn("Failed to save palette to localStorage:", error);
    return null;
  }
};

export const removeSavedPalette = (scopeKey, paletteIndex) => {
  try {
    const existing = getSavedPalettes(scopeKey);
    if (paletteIndex < 0 || paletteIndex >= existing.length) return existing;

    const updated = existing.filter((_, i) => i !== paletteIndex);

    if (updated.length === 0) {
      localStorage.removeItem(buildSavedPalettesStorageKey(scopeKey));
    } else {
      localStorage.setItem(
        buildSavedPalettesStorageKey(scopeKey),
        JSON.stringify(updated),
      );
    }

    dispatchSavedPalettesUpdate(scopeKey, updated);
    return updated;
  } catch (error) {
    console.warn("Failed to remove saved palette from localStorage:", error);
    return null;
  }
};

export const clearSavedPalettes = (scopeKey) => {
  try {
    localStorage.removeItem(buildSavedPalettesStorageKey(scopeKey));
    dispatchSavedPalettesUpdate(scopeKey, []);
  } catch (error) {
    console.warn("Failed to clear saved palettes from localStorage:", error);
  }
};
